<template>
  <div class="pt-safe relative z-30 flex items-center py-4 px-safe bg-background/50 xl:bg-white/80 dark:xl:bg-black/60 backdrop-blur-xl"
    :class="[appStore.plateform]">

    <!-- Drawer button (3+ centers) -->
    <button v-if="showDrawer"
      class="header-drawer-btn w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mr-3"
      @click="navigationDrawerStore.open()">
      <Bars3Icon class="h-5 w-5 text-muted-foreground" />
    </button>

    <!-- Desktop multi-center: horizontal center tabs -->
    <TopbarCenterTabs v-if="isMultiCenter" class="flex-1 min-w-0" />

    <!-- Otherwise: static title -->
    <div v-else class="min-w-0" :class="isDesktop ? 'shrink-0' : 'flex-1'">
      <div class="flex items-center gap-2">
        <h1 class="text-lg font-bold text-foreground leading-tight truncate">{{ title }}</h1>

        <!-- Time badge -->
        <div v-if="shouldShowTime && centerLocalTime"
          class="header-time-badge inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium text-muted-foreground shrink-0">
          <ClockIcon class="h-3 w-3" style="color: #06C8FF;" />
          {{ centerLocalTime }}
        </div>
      </div>
    </div>

    <!-- Avatar link to profile (hidden on desktop — shown in sidebar instead) -->
    <router-link v-if="!isDesktop" :to="{ name: 'Profile' }" class="shrink-0 ml-3">
      <UserAvatar
        :name="avatarName"
        :color="avatarColor"
        size="sm" />
    </router-link>

    <!-- NavigationDrawer -->
    <NavigationDrawer v-if="showDrawer">
      <template #headline>
        {{ appStore.name }}
      </template>
    </NavigationDrawer>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Bars3Icon, ClockIcon } from '@heroicons/vue/24/outline'
import { useUserManagerStore } from '../../stores/userManagerStore'
import { useAppSettingsStore } from '../../stores/AppSettingsStore'
import { useNavigationDrawerStore } from '../../stores/NavigationDrawerStore'
import NavigationDrawer from '../Navigation/NavigationDrawer.vue'
import UserAvatar from '../UserAvatar.vue'
import TopbarCenterTabs from '../desktop/TopbarCenterTabs.vue'

const userManagerStore = useUserManagerStore()
const appStore = useAppSettingsStore()
const navigationDrawerStore = useNavigationDrawerStore()

const props = defineProps<{
  title: string
  showDrawer: boolean
  centerLocalTime: string
  shouldShowTime: boolean
  currentCenterId: number | null
  isDesktop: boolean
}>()

const isMultiCenter = computed(() => props.isDesktop && userManagerStore.displayedCenters.length > 1)

const currentCenter = computed(() => {
  if (!props.currentCenterId) return null
  return userManagerStore.user?.centers.find(c => c.id === props.currentCenterId) ?? null
})

const avatarName = computed(() => {
  return currentCenter.value?.connection?.name?.trim() || props.title || 'U'
})

const avatarColor = computed(() => {
  return currentCenter.value?.connection?.color || '#06C8FF'
})

</script>

<style scoped>
.header-drawer-btn {
  background: var(--card);
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}

:is(.dark *).header-drawer-btn {
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.3);
}

.header-time-badge {
  background: rgba(6, 200, 255, 0.06);
  border: 1px solid rgba(6, 200, 255, 0.10);
}

:is(.dark *).header-time-badge {
  background: rgba(6, 200, 255, 0.08);
  border: 1px solid rgba(6, 200, 255, 0.14);
}

</style>
